// SPDX-License-Identifier: AGPL-3.0-or-later

import {z} from 'zod';
import {type MondayInline, parseMondayMarkdown} from './MondayMarkdown';

export const MONDAY_SECTION_KINDS = ['text', 'list', 'time'] as const;
export type MondaySectionKind = (typeof MONDAY_SECTION_KINDS)[number];
/** Where a task list came from in ClickUp. `worked` = time logged, status unchanged. */
export const MONDAY_TASK_STATUSES = ['done', 'invoiced', 'review', 'started', 'worked'] as const;
export type MondayTaskStatus = (typeof MONDAY_TASK_STATUSES)[number];
export const MONDAY_STATUS_LABELS: Record<MondayTaskStatus, string> = {
	done: 'Completed',
	invoiced: 'To be invoiced',
	review: 'In review',
	started: 'In progress',
	worked: 'Worked on',
};
/** A tick the writer puts on a row: done is ticked and struck through, the others show as a pill. */
export const MONDAY_ITEM_STATES = ['done', 'progress', 'hold'] as const;
export type MondayItemState = (typeof MONDAY_ITEM_STATES)[number];
export const MONDAY_ITEM_STATE_LABELS: Record<MondayItemState, string> = {
	done: 'Done',
	progress: 'In progress',
	hold: 'On hold',
};

const HttpUrl = z
	.string()
	.trim()
	.max(2000)
	.refine((value) => /^https?:\/\/\S+$/i.test(value), 'Links must start with http:// or https://');
const Item = z.object({
	id: z.string().max(80),
	text: z.string().max(500),
	url: HttpUrl.nullable().default(null),
	minutes: z.number().int().min(0).max(100000).nullable().default(null),
	/** Small grey text after the item, such as the ClickUp list of a time row. */
	note: z.string().max(160).default(''),
	state: z.enum(MONDAY_ITEM_STATES).nullable().default(null),
});
// Drafts saved before sections had kinds parse as plain text sections.
const Section = z.object({
	id: z.string().max(80),
	title: z.string().max(160),
	body: z.string().max(12000).default(''),
	kind: z.enum(MONDAY_SECTION_KINDS).default('text'),
	items: z.array(Item).max(80).default([]),
	status: z.enum(MONDAY_TASK_STATUSES).nullable().default(null),
	/** Set on sections Konvo fills in (focus, time_day, ...), so a saved layout finds them after a rename. */
	ref: z.string().max(40).nullable().default(null),
	hidden: z.boolean().default(false),
});
const Options = z.object({
	/** Counts and hours as tiles under the heading. */
	summary: z.boolean().default(true),
	/** Hours next to tasks in project lists. */
	hours: z.boolean().default(true),
});
export const MondayReportDraftSchema = z.object({
	subject: z.string().trim().min(1).max(255),
	heading: z.string().max(200),
	weekLabel: z.string().max(100),
	intro: z.string().max(4000),
	signoff: z.string().max(1000).default(''),
	palette: z.enum(['light', 'dark']),
	// Zod 4 does not parse a default value, so spell the inner defaults out.
	options: Options.default({summary: true, hours: true}),
	recipients: z
		.array(z.object({name: z.string().max(160).nullable(), address: z.string().trim().email().max(254)}))
		.max(20),
	projects: z
		.array(
			z.object({
				id: z.string().max(80),
				name: z.string().max(200),
				sections: z.array(Section).max(12),
				hidden: z.boolean().default(false),
			}),
		)
		.max(40),
	sections: z.array(Section).max(20),
});
export type MondayReportDraft = z.infer<typeof MondayReportDraftSchema>;
export type MondayDraftSection = MondayReportDraft['sections'][number];
export type MondayDraftItem = MondayDraftSection['items'][number];
export type MondayDraftProject = MondayReportDraft['projects'][number];

/** The layout a user keeps from week to week: section order, kinds, visibility and options. */
const TemplateSection = z.object({
	title: z.string().max(160),
	kind: z.enum(MONDAY_SECTION_KINDS),
	status: z.enum(MONDAY_TASK_STATUSES).nullable().default(null),
	ref: z.string().max(40).nullable().default(null),
	hidden: z.boolean().default(false),
});
export const MondayReportTemplateSchema = z.object({
	palette: z.enum(['light', 'dark']),
	options: Options.default({summary: true, hours: true}),
	signoff: z.string().max(1000).default(''),
	sections: z.array(TemplateSection).max(20),
	projectSections: z.array(TemplateSection).max(12),
});
export type MondayReportTemplate = z.infer<typeof MondayReportTemplateSchema>;

function templateSection(section: MondayDraftSection): MondayReportTemplate['sections'][number] {
	return {title: section.title, kind: section.kind, status: section.status, ref: section.ref, hidden: section.hidden};
}

/** How a saved layout recognises a section from week to week. */
export function mondaySectionKey(section: {
	title: string;
	kind: MondaySectionKind;
	status: MondayTaskStatus | null;
	ref: string | null;
}): string {
	return section.status ?? section.ref ?? `${section.kind}:${section.title.trim().toLowerCase()}`;
}

/** The reusable part of a draft. Project layout merges every project's sections in first-seen order. */
export function mondayTemplateFromDraft(draft: MondayReportDraft): MondayReportTemplate {
	const seen = new Map<string, MondayReportTemplate['projectSections'][number]>();
	for (const project of draft.projects) {
		for (const section of project.sections) {
			const key = mondaySectionKey(section);
			if (!seen.has(key)) seen.set(key, templateSection(section));
		}
	}
	return {
		palette: draft.palette,
		options: draft.options,
		signoff: draft.signoff,
		sections: draft.sections.map(templateSection),
		projectSections: [...seen.values()].slice(0, 12),
	};
}

export function sectionMinutes(section: MondayDraftSection): number {
	return section.items.reduce((total, item) => total + (item.minutes ?? 0), 0);
}

export function projectMinutes(project: MondayDraftProject): number {
	return project.sections
		.filter((section) => section.kind === 'list' && !section.hidden)
		.reduce((total, section) => total + sectionMinutes(section), 0);
}

export function formatMinutes(minutes: number): string {
	const hours = Math.floor(minutes / 60);
	const rest = Math.round(minutes % 60);
	if (hours === 0) return `${rest}m`;
	return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

export interface MondaySummaryTile {
	label: string;
	value: string;
}

/** Tiles under the heading: tasks per status across visible projects, then hours logged. */
export function mondaySummary(draft: MondayReportDraft): Array<MondaySummaryTile> {
	const counts = new Map<MondayTaskStatus, number>();
	for (const project of draft.projects) {
		if (project.hidden) continue;
		for (const section of project.sections) {
			if (section.hidden || section.kind !== 'list' || !section.status) continue;
			const filled = section.items.filter((item) => item.text.trim()).length;
			counts.set(section.status, (counts.get(section.status) ?? 0) + filled);
		}
	}
	const tiles: Array<MondaySummaryTile> = [];
	for (const status of MONDAY_TASK_STATUSES) {
		const count = counts.get(status) ?? 0;
		if (count > 0) tiles.push({label: MONDAY_STATUS_LABELS[status], value: String(count)});
	}
	// Both time tables add up to the same total; take the largest in case one was trimmed.
	const minutes = Math.max(
		0,
		...draft.sections
			.filter((section) => section.kind === 'time' && !section.hidden)
			.map((section) => sectionMinutes(section)),
	);
	if (minutes > 0) tiles.push({label: 'Time logged', value: formatMinutes(minutes)});
	return tiles.slice(0, 5);
}

export function sectionHasContent(section: MondayDraftSection): boolean {
	if (section.kind === 'text') return section.body.trim().length > 0;
	return section.items.some((item) => item.text.trim());
}

function escapeReportText(value: string): string {
	return value.replace(
		/[&<>"']/g,
		(char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[char]!,
	);
}

interface Palette {
	bg: string;
	surface: string;
	text: string;
	muted: string;
	border: string;
	track: string;
	fill: string;
	link: string;
	dots: Record<MondayTaskStatus, string>;
	pills: Record<'progress' | 'hold', {bg: string; text: string}>;
}

const LIGHT: Palette = {
	bg: '#f4f4f5',
	surface: '#ffffff',
	text: '#18181b',
	muted: '#71717a',
	border: '#e4e4e7',
	track: '#f1f1f3',
	fill: '#52525b',
	link: '#18181b',
	dots: {done: '#3f9d6b', invoiced: '#6b7fd7', review: '#c28a2c', started: '#8b8b94', worked: '#b4b4bb'},
	pills: {progress: {bg: '#e8edfb', text: '#3a51a3'}, hold: {bg: '#fbefdc', text: '#8a5512'}},
};
const DARK: Palette = {
	bg: '#101012',
	surface: '#1b1b1f',
	text: '#f4f4f5',
	muted: '#a1a1aa',
	border: '#2e2e34',
	track: '#26262b',
	fill: '#d4d4d8',
	link: '#f4f4f5',
	dots: {done: '#5fbf8a', invoiced: '#8ea0ec', review: '#dca850', started: '#8b8b94', worked: '#6b6b73'},
	pills: {progress: {bg: '#262f4d', text: '#b3c2f4'}, hold: {bg: '#3b2f1c', text: '#e8bd78'}},
};

function renderInline(parts: Array<MondayInline>, palette: Palette): string {
	return parts
		.map((part) => {
			const text = escapeReportText(part.text);
			if (part.href) return `<a href="${text}" style="color:${palette.link};text-decoration:underline">${text}</a>`;
			if (part.bold) return `<strong>${text}</strong>`;
			if (part.italic) return `<em>${text}</em>`;
			if (part.strike) return `<s>${text}</s>`;
			return text;
		})
		.join('');
}

/** A 14px box for to-dos and ticked rows; tables and inline styles so Outlook draws it too. */
function checkBox(done: boolean, palette: Palette): string {
	return done
		? `<div style="width:14px;height:14px;border-radius:4px;background:${palette.dots.done};color:#ffffff;font-size:11px;font-weight:700;line-height:14px;text-align:center">&#10003;</div>`
		: `<div style="width:12px;height:12px;border-radius:4px;border:1px solid ${palette.muted}"></div>`;
}

function markerRow(marker: string, body: string, depth: number, markerWidth = 22): string {
	return `<table role="presentation" cellspacing="0" cellpadding="0" style="margin:2px 0 2px ${depth * 20}px"><tr><td width="${markerWidth}" valign="top" style="padding-top:3px">${marker}</td><td style="line-height:1.6;overflow-wrap:anywhere">${body}</td></tr></table>`;
}

/** Text fields as Markdown: headings, bullets, numbers, to-dos (ticked ones struck through) and inline styles. */
function renderMarkdown(value: string, palette: Palette): string {
	return parseMondayMarkdown(value)
		.map((block) => {
			switch (block.kind) {
				case 'heading': {
					const size = block.level === 1 ? 16 : block.level === 2 ? 15 : 14;
					return `<div style="font-size:${size}px;font-weight:${block.level === 3 ? 600 : 700};line-height:1.35;margin:12px 0 4px;overflow-wrap:anywhere">${renderInline(block.inline, palette)}</div>`;
				}
				case 'check': {
					const text = renderInline(block.inline, palette);
					const body = block.done
						? `<span style="text-decoration:line-through;color:${palette.muted}">${text}</span>`
						: text;
					return markerRow(checkBox(block.done, palette), body, block.depth);
				}
				case 'bullet':
					return markerRow(
						`<div style="width:5px;height:5px;border-radius:3px;background:${palette.text};margin:6px 0 0 4px"></div>`,
						renderInline(block.inline, palette),
						block.depth,
					);
				case 'number':
					return markerRow(
						`<span style="color:${palette.muted};font-variant-numeric:tabular-nums">${escapeReportText(block.label)}</span>`,
						renderInline(block.inline, palette),
						block.depth,
						26,
					);
				case 'paragraph':
					return `<div style="line-height:1.6;margin:2px 0;overflow-wrap:anywhere">${block.lines.map((line) => renderInline(line, palette)).join('<br>')}</div>`;
				case 'gap':
					return '<div style="height:10px;line-height:10px;font-size:1px">&nbsp;</div>';
			}
		})
		.join('');
}

function renderItems(section: MondayDraftSection, palette: Palette, showHours: boolean): string {
	const dot = section.status ? palette.dots[section.status] : palette.muted;
	const rows = section.items
		.filter((item) => item.text.trim())
		.map((item) => {
			const done = item.state === 'done';
			const text = done
				? `<span style="text-decoration:line-through;color:${palette.muted}">${escapeReportText(item.text)}</span>`
				: escapeReportText(item.text);
			const label = item.url
				? `<a href="${escapeReportText(item.url)}" style="color:${palette.link};text-decoration:none;border-bottom:1px solid ${palette.border}">${text}</a>`
				: text;
			const note = item.note.trim()
				? ` <span style="color:${palette.muted}">· ${escapeReportText(item.note)}</span>`
				: '';
			const pill =
				item.state === 'progress' || item.state === 'hold'
					? ` <span style="display:inline-block;padding:0 8px;border-radius:999px;font-size:11px;font-weight:600;line-height:18px;white-space:nowrap;background:${palette.pills[item.state].bg};color:${palette.pills[item.state].text}">${MONDAY_ITEM_STATE_LABELS[item.state]}</span>`
					: '';
			const hours = showHours && item.minutes ? escapeReportText(formatMinutes(item.minutes)) : '';
			const marker = done
				? `<div style="margin-top:3px">${checkBox(true, palette)}</div>`
				: `<div style="width:6px;height:6px;border-radius:3px;background:${dot};margin:8px 0 0 4px"></div>`;
			return `<tr><td width="22" valign="top" style="padding:5px 0 0">${marker}</td><td style="padding:5px 12px 5px 0;line-height:1.5;overflow-wrap:anywhere">${label}${pill}${note}</td><td align="right" valign="top" style="padding:5px 0;color:${palette.muted};white-space:nowrap;font-variant-numeric:tabular-nums">${hours}</td></tr>`;
		})
		.join('');
	return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0">${rows}</table>`;
}

function renderTime(section: MondayDraftSection, palette: Palette): string {
	const items = section.items.filter((item) => item.text.trim());
	const most = Math.max(1, ...items.map((item) => item.minutes ?? 0));
	const rows = items
		.map((item) => {
			const width = Math.max(2, Math.round(((item.minutes ?? 0) / most) * 100));
			const note = item.note.trim()
				? `<div style="color:${palette.muted};font-size:12px">${escapeReportText(item.note)}</div>`
				: '';
			return `<tr><td style="padding:6px 12px 6px 0;line-height:1.4;overflow-wrap:anywhere">${escapeReportText(item.text)}${note}</td><td width="34%" style="padding:6px 12px 6px 0"><div style="background:${palette.track};border-radius:4px;height:8px"><div style="background:${palette.fill};border-radius:4px;height:8px;width:${width}%"></div></div></td><td align="right" style="padding:6px 0;white-space:nowrap;font-variant-numeric:tabular-nums">${escapeReportText(formatMinutes(item.minutes ?? 0))}</td></tr>`;
		})
		.join('');
	const total = `<tr><td colspan="2" style="padding:10px 0 0;border-top:1px solid ${palette.border};font-weight:600">Total</td><td align="right" style="padding:10px 0 0;border-top:1px solid ${palette.border};font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums">${escapeReportText(formatMinutes(sectionMinutes(section)))}</td></tr>`;
	return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0">${rows}${total}</table>`;
}

function renderSection(
	section: MondayDraftSection,
	palette: Palette,
	showHours: boolean,
	level: 'report' | 'project',
): string {
	if (section.hidden || !sectionHasContent(section)) return '';
	const title =
		level === 'project'
			? `<div style="font-size:12px;font-weight:600;color:${palette.muted};margin:0 0 4px">${escapeReportText(section.title)}</div>`
			: `<h2 style="font-size:16px;line-height:1.3;margin:0 0 10px;overflow-wrap:anywhere">${escapeReportText(section.title)}</h2>`;
	const body =
		section.kind === 'text'
			? renderMarkdown(section.body, palette)
			: section.kind === 'time'
				? renderTime(section, palette)
				: renderItems(section, palette, showHours);
	return level === 'project'
		? `<div style="margin:14px 0 0">${title}${body}</div>`
		: `<div style="border-top:1px solid ${palette.border};padding-top:22px;margin-top:26px">${title}${body}</div>`;
}

function renderSummary(draft: MondayReportDraft, palette: Palette): string {
	if (!draft.options.summary) return '';
	const tiles = mondaySummary(draft);
	if (tiles.length === 0) return '';
	const cells = tiles
		.map(
			(tile) =>
				`<td valign="top" style="padding:0 4px"><div style="border:1px solid ${palette.border};border-radius:12px;padding:12px 14px"><div style="font-size:22px;font-weight:700;line-height:1.1;font-variant-numeric:tabular-nums">${escapeReportText(tile.value)}</div><div style="font-size:12px;color:${palette.muted};margin-top:4px">${escapeReportText(tile.label)}</div></div></td>`,
		)
		.join('');
	return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:22px -4px 0;table-layout:fixed"><tr>${cells}</tr></table>`;
}

function renderProject(project: MondayDraftProject, palette: Palette, showHours: boolean): string {
	if (project.hidden) return '';
	const sections = project.sections.map((section) => renderSection(section, palette, showHours, 'project')).join('');
	if (!sections) return '';
	const minutes = projectMinutes(project);
	const hours =
		showHours && minutes > 0
			? `<td align="right" style="color:${palette.muted};white-space:nowrap;font-variant-numeric:tabular-nums">${formatMinutes(minutes)}</td>`
			: '';
	return `<div style="border:1px solid ${palette.border};border-radius:14px;padding:16px 18px;margin-top:12px"><table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td style="font-size:15px;font-weight:600;overflow-wrap:anywhere">${escapeReportText(project.name)}</td>${hours}</tr></table>${sections}</div>`;
}

/** The same renderer drives the preview and the sent email. No user HTML is executed. */
export function renderMondayDraft(draft: MondayReportDraft): string {
	const palette = draft.palette === 'dark' ? DARK : LIGHT;
	const showHours = draft.options.hours;
	const projects = draft.projects.map((project) => renderProject(project, palette, showHours)).join('');
	const projectsBlock = projects
		? `<div style="border-top:1px solid ${palette.border};padding-top:22px;margin-top:26px"><h2 style="font-size:16px;line-height:1.3;margin:0 0 2px">Projects</h2>${projects}</div>`
		: '';
	const intro = draft.intro.trim() ? `<div style="margin:18px 0 0">${renderMarkdown(draft.intro, palette)}</div>` : '';
	const signoff = draft.signoff.trim()
		? `<div style="margin-top:26px">${renderMarkdown(draft.signoff, palette)}</div>`
		: '';
	return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><meta charset="utf-8"><meta name="color-scheme" content="${draft.palette}"></head><body style="margin:0;padding:24px 12px;background:${palette.bg};color:${palette.text};font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif">
<table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;max-width:680px;margin:auto;background:${palette.surface};border:1px solid ${palette.border};border-radius:18px;color:${palette.text}"><tr><td style="padding:30px 32px">
<div style="font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${palette.muted}">Monday Madness</div><h1 style="font-size:24px;line-height:1.25;margin:8px 0 4px;overflow-wrap:anywhere">${escapeReportText(draft.heading)}</h1><div style="color:${palette.muted}">${escapeReportText(draft.weekLabel)}</div>
${intro}${renderSummary(draft, palette)}${projectsBlock}
${draft.sections.map((section) => renderSection(section, palette, showHours, 'report')).join('')}
${signoff}<div style="border-top:1px solid ${palette.border};margin-top:28px;padding-top:14px;font-size:12px;color:${palette.muted}">Sent from Konvo</div>
</td></tr></table></body></html>`;
}
